import { RecommendationAcceptanceCard } from '@/components/metrics/RecommendationAcceptanceCard';
import { CoreOutcomeCard } from '@/components/metrics/CoreOutcomeCard';
import { DueOutOutcomesCard } from '@/components/analytics/DueOutOutcomesCard';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Users, Briefcase, Bell, Target, Activity, BarChart3, Pause, UsersRound, Headset } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { TopBar } from '@/components/layout/TopBar';
import { useAuth } from '@/contexts/AuthContext';
import { TechnicianPerformanceCard } from '@/components/metrics/TechnicianPerformanceCard';
import { InspectionPerformanceCard } from '@/components/metrics/InspectionPerformanceCard';
import { MinuteProductivityCard } from '@/components/metrics/MinuteProductivityCard';
import { WaiterPromiseWidget } from '@/components/metrics/WaiterPromiseWidget';
import { AdvisorMetricsDashboard } from '@/components/metrics/AdvisorMetricsDashboard';
import { AdvisorPerformanceDashboard } from '@/components/metrics/AdvisorPerformanceDashboard';
import { JobStatsExplorer } from '@/components/metrics/JobStatsExplorer';
import { TechnicianTrendChart } from '@/components/metrics/TechnicianTrendChart';
import { JobPerformanceTrends } from '@/components/metrics/JobPerformanceTrends';
import { ShopMetricsTrends } from '@/components/metrics/ShopMetricsTrends';
import { AlertSettings } from '@/components/metrics/AlertSettings';
import { AlertHistoryWidget } from '@/components/metrics/AlertHistoryWidget';
import { ShopHealthDashboard } from '@/components/metrics/ShopHealthDashboard';
import { JobCategoryPerformance } from '@/components/metrics/performance/JobCategoryPerformance';
import { MakeModelPerformance } from '@/components/metrics/performance/MakeModelPerformance';
import { YearGroupPerformance } from '@/components/metrics/performance/YearGroupPerformance';
import { PauseTimeOverview } from '@/components/metrics/pause-analysis/PauseTimeOverview';
import { PauseTimeByCategory } from '@/components/metrics/pause-analysis/PauseTimeByCategory';
import { PauseTimeByMakeModel } from '@/components/metrics/pause-analysis/PauseTimeByMakeModel';
import { PauseTimeByYearGroup } from '@/components/metrics/pause-analysis/PauseTimeByYearGroup';
import { DateRangeFilter, getDateRange, DateRangeOption } from '@/components/metrics/shared/DateRangeFilter';
import { TechnicianPreferencesDebug } from '@/components/metrics/TechnicianPreferencesDebug';
import { TeamCapacityCard } from '@/components/teams/TeamCapacityCard';
import { TeamAnalyticsCard } from '@/components/teams/TeamAnalyticsCard';
import { TeamTransferHistory } from '@/components/teams/TeamTransferHistory';
import { useMultiTeam } from '@/hooks/useMultiTeam';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';
import { actsAsLegacyRole } from '@/lib/roles/multiRole';
import { TeamCompatibilityPanel } from '@/components/analytics/TeamCompatibilityPanel';

export default function Analytics() {
  const navigate = useNavigate();
  const { membership, activeShop } = useAuth();
  // Core-100 multi-role: managers, OWNERS and org admins (effective roles). The scalar check hid the
  // manager tabs from owners.
  const isManager = actsAsLegacyRole(membership as never, 'OWNER', 'MANAGER', 'ORG_ADMIN');
  // Batch 3 (FOH Timer Handoff): advisor performance analytics are a
  // management surface — visible to OWNER / MANAGER / ORG_ADMIN, not to
  // SERVICE_WRITER (who can otherwise reach this page). Gated additionally
  // by the `advisorTimingAnalytics` flag, same as the Passport metrics.
  const advisorTimingAnalyticsEnabled = useFeatureFlag('advisorTimingAnalytics');
  const canViewAdvisorPerformance =
    advisorTimingAnalyticsEnabled &&
    (membership?.role === 'OWNER' ||
      membership?.role === 'MANAGER' ||
      membership?.role === 'ORG_ADMIN');
  const { isMultiTeamEnabled } = useMultiTeam();
  const { shopTimezone } = useShopTimezone();
  const shopTodayDate = useMemo(() => {
    const label = getTodayInShopTz(shopTimezone);
    const [y, m, d] = label.split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0);
  }, [shopTimezone]);
  const [selectedTech, setSelectedTech] = useState<string>('');
  const [technicians, setTechnicians] = useState<Array<{ id: string; name: string; technician_profile_id: string | null }>>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState(() => getTodayInShopTz(shopTimezone));
  const [techDateRange, setTechDateRange] = useState<DateRangeOption>('30d');
  const [dateRange, setDateRange] = useState<DateRangeOption>('30d');
  
  const { startDate, endDate } = getDateRange(dateRange, shopTimezone);
  const { startDate: techStartDate, endDate: techEndDate } = getDateRange(techDateRange, shopTimezone);

  // Load technicians and tickets for selection
  useEffect(() => {
    if (activeShop) {
      supabase
        .from('technicians')
        .select('id, name, technician_profile_id')
        .eq('shop_id', activeShop.id)
        .eq('employment_status', 'Active')
        .order('name')
        .then(({ data }) => {
          if (data && data.length > 0) {
            setTechnicians(data);
            // Auto-select first tech with a profile
            const techWithProfile = data.find(t => t.technician_profile_id);
            if (techWithProfile) {
              setSelectedTech(techWithProfile.id);
            }
          }
        });

      // Load tickets for Shop Health Dashboard
      // IV-D-28 R1 FIX-1: narrow columns to what ShopHealthDashboard reads
      // (previously `select('*')` fetched every column for the entire shop
      // ticket history). Also exclude deleted tickets (already filtered
      // client-side at ShopHealthDashboard:29,79) and cap at 5000 rows as
      // defense-in-depth against unbounded growth on long-running shops.
      supabase
        .from('tickets')
        .select('id, parking_lot, scheduled_date, start_time, status, type, predicted_minutes, assigned_tech_id')
        .eq('shop_id', activeShop.id)
        // DMC-D3a: canonical active-ticket filter — both reps excluded.
        .neq('status', 'Deleted')
        .is('deleted_at', null)
        .limit(5000)
        .then(({ data }) => {
          if (data) {
            setTickets(data);
          }
        });
    }
  }, [activeShop]);

  const selectedTechProfile = technicians.find(t => t.id === selectedTech)?.technician_profile_id;

  if (!activeShop) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="p-6">
          <p className="text-muted-foreground">No active shop selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold">Analytics & Metrics</h1>
                <p className="text-muted-foreground">Performance tracking and insights</p>
              </div>
            </div>
          </div>

          {/* Metrics Tabs */}
          <Tabs defaultValue="technicians" className="space-y-6">
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <TabsList className="inline-flex gap-1 min-w-max">
                <TabsTrigger value="technicians" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                  <Users className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">Technicians</span>
                  <span className="sm:hidden">Techs</span>
                </TabsTrigger>
                <TabsTrigger value="advisors" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                  <Briefcase className="h-3 w-3 md:h-4 md:w-4" />
                  Advisors
                </TabsTrigger>
                {canViewAdvisorPerformance && (
                  <TabsTrigger value="advisor-performance" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                    <Headset className="h-3 w-3 md:h-4 md:w-4" />
                    <span className="hidden sm:inline">Advisor Performance</span>
                    <span className="sm:hidden">FOH</span>
                  </TabsTrigger>
                )}
                <TabsTrigger value="jobs" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                  <Target className="h-3 w-3 md:h-4 md:w-4" />
                  Jobs
                </TabsTrigger>
                <TabsTrigger value="performance" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                  <BarChart3 className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">Performance</span>
                  <span className="sm:hidden">Perf</span>
                </TabsTrigger>
                <TabsTrigger value="pause-analysis" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                  <Pause className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">Pause Analysis</span>
                  <span className="sm:hidden">Pause</span>
                </TabsTrigger>
                <TabsTrigger value="trends" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                  <TrendingUp className="h-3 w-3 md:h-4 md:w-4" />
                  Trends
                </TabsTrigger>
                {isManager && (
                  <TabsTrigger value="health" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                    <Activity className="h-3 w-3 md:h-4 md:w-4" />
                    <span className="hidden sm:inline">Shop Health</span>
                    <span className="sm:hidden">Health</span>
                  </TabsTrigger>
                )}
                {isManager && (
                  <TabsTrigger value="alerts" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                    <Bell className="h-3 w-3 md:h-4 md:w-4" />
                    Alerts
                  </TabsTrigger>
                )}
                {isManager && (
                  <TabsTrigger value="team-compatibility" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                    <UsersRound className="h-3 w-3 md:h-4 md:w-4" />
                    <span className="hidden sm:inline">Team compatibility</span>
                    <span className="sm:hidden">Pairs</span>
                  </TabsTrigger>
                )}
                {isMultiTeamEnabled && isManager && (
                  <TabsTrigger value="teams" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3">
                    <UsersRound className="h-3 w-3 md:h-4 md:w-4" />
                    Teams
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* Technician Performance Tab */}
            <TabsContent value="technicians" className="space-y-6">
              {/* Filters Row */}
              <Card>
                <CardHeader>
                  <CardTitle>Technician Metrics</CardTitle>
                  <CardDescription>View performance and minute productivity metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="min-w-[180px]">
                      <Label>Technician</Label>
                      <Select value={selectedTech} onValueChange={setSelectedTech}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select technician..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Technicians</SelectItem>
                          {technicians.map((tech) => (
                            <SelectItem key={tech.id} value={tech.id}>
                              {tech.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <DateRangeFilter value={techDateRange} onChange={setTechDateRange} />
                  </div>
                </CardContent>
              </Card>

              {/* Minute Productivity Card - always shown */}
              <MinuteProductivityCard
                shopId={activeShop.id}
                technicianProfileId={selectedTech === 'all' ? undefined : selectedTechProfile || undefined}
                startDate={techStartDate}
                endDate={techEndDate}
              />

              {/* Performance Card - only for specific tech */}
              {selectedTechProfile && selectedTech !== 'all' && (
                <div className="grid gap-6">
                  <TechnicianPerformanceCard
                    technicianProfileId={selectedTechProfile}
                    shopId={activeShop.id}
                    startDate={techStartDate}
                    endDate={techEndDate}
                  />
                  <InspectionPerformanceCard
                    shopId={activeShop.id}
                    technicianProfileId={selectedTechProfile}
                    startDate={techStartDate}
                    endDate={techEndDate}
                  />
                  <TechnicianPreferencesDebug
                    technicianId={selectedTech}
                    shopId={activeShop.id}
                  />
                </div>
              )}

              {/* Inspection Performance - all technicians */}
              {selectedTech === 'all' && (
                <InspectionPerformanceCard
                  shopId={activeShop.id}
                  startDate={techStartDate}
                  endDate={techEndDate}
                />
              )}
            </TabsContent>

            {/* Advisor Metrics Tab */}
            <TabsContent value="advisors" className="space-y-6">
              <AdvisorMetricsDashboard 
                shopId={activeShop.id}
                dateISO={selectedDate}
                shopTimezone={shopTimezone}
              />
              <WaiterPromiseWidget shopId={activeShop.id} />
            </TabsContent>

            {/* Advisor Performance Tab (FOH / Advisor Analytics) —
                OWNER / MANAGER / ORG_ADMIN only, flag-gated */}
            {canViewAdvisorPerformance && (
              <TabsContent value="advisor-performance" className="space-y-6">
                <AdvisorPerformanceDashboard shopId={activeShop.id} />
              </TabsContent>
            )}

            {/* Job Statistics Tab */}
            <TabsContent value="jobs" className="space-y-6">
              <JobStatsExplorer shopId={activeShop.id} />
            </TabsContent>

            {/* Job/Vehicle/Year Performance Tab */}
            <TabsContent value="performance" className="space-y-6">
              <div className="flex justify-end">
                <DateRangeFilter value={dateRange} onChange={setDateRange} />
              </div>
              <JobCategoryPerformance shopId={activeShop.id} startDate={startDate} endDate={endDate} shopTimezone={shopTimezone} />
              <MakeModelPerformance shopId={activeShop.id} startDate={startDate} endDate={endDate} shopTimezone={shopTimezone} />
              <YearGroupPerformance shopId={activeShop.id} startDate={startDate} endDate={endDate} shopTimezone={shopTimezone} />
            </TabsContent>

            {/* Pause-Time Analysis Tab */}
            <TabsContent value="pause-analysis" className="space-y-6">
              <div className="flex justify-end">
                <DateRangeFilter value={dateRange} onChange={setDateRange} />
              </div>
              <PauseTimeOverview shopId={activeShop.id} startDate={startDate} endDate={endDate} shopTimezone={shopTimezone} />
              <PauseTimeByCategory shopId={activeShop.id} startDate={startDate} endDate={endDate} shopTimezone={shopTimezone} />
              <PauseTimeByMakeModel shopId={activeShop.id} startDate={startDate} endDate={endDate} shopTimezone={shopTimezone} />
              <PauseTimeByYearGroup shopId={activeShop.id} startDate={startDate} endDate={endDate} shopTimezone={shopTimezone} />
            </TabsContent>

            {/* Historical Trends Tab */}
            <TabsContent value="trends" className="space-y-6">
              {selectedTechProfile && (
                <TechnicianTrendChart 
                  technicianProfileId={selectedTechProfile}
                  shopId={activeShop.id}
                />
              )}
              <JobPerformanceTrends shopId={activeShop.id} />
              <ShopMetricsTrends shopId={activeShop.id} />
            </TabsContent>

            {/* Shop Health Dashboard Tab (Manager Only) */}
            {isManager && (
              <TabsContent value="health" className="space-y-6">
                <ShopHealthDashboard 
                  shopId={activeShop.id}
                  selectedDate={shopTodayDate}
                  technicians={technicians}
                  tickets={tickets}
                />
                {/* Core-100 R2 CORE-BC-GAP-006: acceptance measured per recommendation kind. */}
                <RecommendationAcceptanceCard shopId={activeShop.id} />
                {/* Core-100 R2 CORE-BD: the nineteen questions together, partial ones labelled, and the measured outcome. */}
                <CoreOutcomeCard shopId={activeShop.id} />
                {/* Core-100 R3 CORE-BH: how promises found in trouble turned out (server-recorded episodes). */}
                <DueOutOutcomesCard shopId={activeShop.id} />
              </TabsContent>
            )}

            {/* Alert Management Tab (Manager Only) */}
            {isManager && (
              <TabsContent value="alerts" className="space-y-6">
                <div className="grid gap-6">
                  <AlertSettings shopId={activeShop.id} />
                  <AlertHistoryWidget shopId={activeShop.id} />
                </div>
              </TabsContent>
            )}

            {/* Core-100 CORE-P: team compatibility (managers / owners only; server re-checks) */}
            {isManager && (
              <TabsContent value="team-compatibility" className="space-y-6">
                <TeamCompatibilityPanel />
              </TabsContent>
            )}

            {/* Team Analytics Tab (Enterprise Multi-Team Only) */}
            {isMultiTeamEnabled && isManager && (
              <TabsContent value="teams" className="space-y-6">
                <TeamCapacityCard />
                <TeamAnalyticsCard />
                <TeamTransferHistory />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
